/**
 * Core API hook with authentication
 *
 * Provides the base hook with token management and apiService access.
 * All specialized hooks build on top of this.
 *
 * @module @shared/services/hooks/useApiCore
 */

import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { apiService } from '../apiService';

/**
 * Hook that provides core API functionality with automatic authentication.
 * This is the base hook that all domain-specific hooks use.
 *
 * @returns Object with token access and apiService
 */
export function useApiCore(): {
    getToken: () => Promise<string>;
    apiService: typeof apiService;
} {
    const { getAccessTokenSilently } = useAuth0();

    // Helper to get token and call apiService
    // Note: This will throw an error if not authenticated, which should be
    // caught by the calling code or by ProtectedRoute
    const getToken = useCallback(async (): Promise<string> => {
        return await getAccessTokenSilently();
    }, [getAccessTokenSilently]);

    return {
        getToken,
        apiService,
    };
}
